import { useEffect, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import type { Upload } from "tus-js-client";
import { useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  authCacheScope,
  createOperationalUploadSession,
  getOperationalUploadSession,
  operationalKeys,
  pb,
} from "@rekonas/data-access";
import type { OperationalUploadSession } from "@rekonas/domain";
import { Alert, Button, Progress, Spinner } from "@rekonas/ui";
import { createTusUpload, findMatchingPreviousUpload, uploadFailure, validateUploadFile, transportUrl, ResumeConflict } from "./tusUpload";
import { archiveIdentity, durableIdempotencyKey } from "./uploadIdentity";

type TransferState = "preparing" | "discovering" | "uploading" | "paused" | "finalizing" | "accepted" | "error" | "resuming" | "retrying" | "reconciling" | "stale" | "conflict";

interface Transfer {
  state: TransferState;
  file: File;
  idempotencyKey: string;
  identity: string;
  restarted?: boolean;
  session: OperationalUploadSession | null;
  progress: number;
  resumed: boolean;
  error?: string;
}

export interface UploadPanelProps {
  workspaceRef: string;
}

export function isAcquisitionArchive(file: File): boolean {
  return file.size > 0 && /^[A-Za-z0-9][A-Za-z0-9_.-]{0,235}\.tgz$/.test(file.name);
}

function fileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const delay = (milliseconds: number) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

function uploadErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.details && typeof error.details === "object" &&
      "error" in error.details && error.details.error && typeof error.details.error === "object" &&
      "code" in error.details.error) {
    if (["idempotency_conflict", "upload_destination_conflict"].includes(String(error.details.error.code))) {
      return "The saved upload destination or archive no longer matches. Ask an operator to check it before starting a fresh upload.";
    }
    if (error.details.error.code === "upload_context_unavailable") {
      return "This account has no complete upload destination. Ask an operator to provision uploader access.";
    }
    if (error.details.error.code === "upload_context_ambiguous") {
      return "This account has more than one upload destination. Ask an operator to correct uploader access.";
    }
  }
  return error instanceof Error ? error.message : "Could not create the upload session.";
}

export function UploadPanel({ workspaceRef }: UploadPanelProps) {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const activeUpload = useRef<Upload | null>(null);
  const finalizationRequest = useRef<AbortController | null>(null);
  const operation = useRef(0);
  const mounted = useRef(true);
  const [transfer, setTransfer] = useState<Transfer | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  useEffect(() => {
    mounted.current = true;
    const scope = authCacheScope();
    const unsubscribe = pb.authStore.onChange(() => {
      if (authCacheScope() !== scope) {
        operation.current += 1;
        finalizationRequest.current?.abort();
        void activeUpload.current?.abort(false).catch(() => undefined);
        setTransfer(null);
      }
    });
    return () => {
      mounted.current = false;
      operation.current += 1;
      unsubscribe();
      finalizationRequest.current?.abort();
      void activeUpload.current?.abort(false).catch(() => undefined);
    };
  }, []);

  async function waitForAcceptance(uploadRef: string) {
    const sequence = operation.current;
    finalizationRequest.current?.abort();
    const controller = new AbortController();
    finalizationRequest.current = controller;
    for (let attempt = 0; attempt < 120; attempt += 1) {
      if (!mounted.current || controller.signal.aborted || sequence !== operation.current) return;
      let session: OperationalUploadSession;
      try {
        session = await getOperationalUploadSession(uploadRef, controller.signal);
      } catch (error) {
        if (controller.signal.aborted) return;
        throw error;
      }
      if (!mounted.current || controller.signal.aborted || sequence !== operation.current) return;
      setTransfer((current) => current ? { ...current, session, state: session.state === "accepted" ? "accepted" : ["rejected", "conflict", "expired"].includes(session.state) ? "conflict" : "finalizing" } : current);
      if (session.state === "accepted") {
        await queryClient.invalidateQueries({ queryKey: operationalKeys.list(workspaceRef) });
        return;
      }
      if (["rejected", "conflict", "expired"].includes(session.state)) {
        throw new Error(session.error?.code || `Upload ${session.state}`);
      }
      await delay(Math.min(5_000, 750 + attempt * 250));
    }
    throw new Error("The archive was uploaded, but final acceptance is still pending. Retry to check again.");
  }

  async function begin(file: File, session: OperationalUploadSession, identity: string, reconcile = false, recovery = 0) {
    const sequence = operation.current;
    const valid = () => mounted.current && sequence === operation.current;
    const update: typeof setTransfer = (value) => { if (valid()) setTransfer(value); };
    try {
      await activeUpload.current?.abort(false);
      if (!valid()) return;
      finalizationRequest.current?.abort();
      update((current) => current ? { ...current, state: "reconciling", error: undefined } : current);
      if (reconcile) session = await getOperationalUploadSession(session.upload_ref);
      if (!valid()) return;
      validateUploadFile(session, file);
      update((current) => current ? { ...current, session } : current);
      if (session.state === "accepted" || session.state === "finalizing") {
        await waitForAcceptance(session.upload_ref);
        return;
      }
      if (session.state === "expired") {
        update((current) => current ? { ...current, state: "stale", error: "The saved upload expired. You can deliberately start a fresh upload." } : current);
        return;
      }
      if (["rejected", "conflict"].includes(session.state)) {
        update((current) => current ? { ...current, state: "conflict",
          error: `This upload cannot resume (${session.error?.code || session.state}). Ask an operator to check it before starting a fresh upload.` } : current);
        return;
      }
      if (session.state === "uploading" && !session.tus.upload_url) {
        update((current) => current ? { ...current, state: "error",
          error: "The server is still locating the upload. Retry to check again. If it remains unavailable, ask an operator to expire the abandoned session." } : current);
        return;
      }
      const currentSession = session;
      const upload = createTusUpload({
        session, identity, token: pb.authStore.token, file,
        onRetry: (conflict) => update((current) => current ? { ...current,
          state: conflict ? "reconciling" : "retrying" } : current),
        onError: (error) => {
          if (!valid()) return;
          void (async () => {
            const failure = uploadFailure(error);
            if (failure.terminal) {
              update((current) => current ? { ...current, state: "conflict",
                error: "The saved upload does not match this archive or has invalid server state. Ask an operator to check it before starting a fresh upload." } : current);
              return;
            }
            update((current) => current ? { ...current, state: "reconciling" } : current);
            const latest = await getOperationalUploadSession(currentSession.upload_ref);
            if (!valid()) return;
            if (latest.state === "accepted" || latest.state === "finalizing") {
              await waitForAcceptance(latest.upload_ref);
            } else if (failure.status === 404 || failure.status === 410) {
              update((current) => current ? { ...current, session: latest, state: "stale",
                error: "The saved transfer is no longer available. Start a fresh upload deliberately, or ask an operator to check it." } : current);
            } else if (failure.method === "POST" && recovery === 0 && latest.tus.upload_url) {
              await begin(file, latest, identity, false, recovery + 1);
            } else {
              update((current) => current ? { ...current, session: latest,
                state: failure.status === 409 || failure.status === 400 || failure.status === 403 ? "conflict" : "error",
                error: failure.status === 409 ? "The upload state still conflicts after checking the server. Close other upload tabs and ask an operator to check it." :
                  failure.status === 401 ? "Sign in again, then resume this upload." :
                  "The upload could not continue. Retry to check the server, or ask an operator for help." } : current);
            }
          })().catch((cause) => update((current) => current ? { ...current, state: "error", error: uploadErrorMessage(cause) } : current));
        },
        onProgress: (uploaded, total) => update((current) => current ? {
          ...current, state: "uploading", progress: total > 0 ? (uploaded / total) * 100 : 0,
        } : current),
        onSuccess: () => {
          if (!valid()) return;
          update((current) => current ? { ...current, state: "finalizing", progress: 100 } : current);
          void waitForAcceptance(currentSession.upload_ref).catch((error) => update((current) => current ? {
            ...current, state: "error", error: uploadErrorMessage(error),
          } : current));
        },
      });
      activeUpload.current = upload;
      const previous = await upload.findPreviousUploads();
      if (!valid()) { await upload.abort(false); return; }
      const match = session.tus.upload_url ? findMatchingPreviousUpload(previous, session) : undefined;
      if (match) upload.resumeFromPreviousUpload(match);
      update((current) => current ? { ...current,
        state: session.tus.upload_url ? "resuming" : "uploading", resumed: Boolean(session.tus.upload_url), error: undefined } : current);
      upload.start();
    } catch (error) {
      update((current) => current ? { ...current, state: error instanceof ResumeConflict ? "conflict" : "error", error: uploadErrorMessage(error) } : current);
    }
  }

  async function prepare(file: File, idempotencyKey: string, identity: string, restarted = false) {
    const sequence = operation.current;
    setTransfer({ state: "preparing", file, idempotencyKey, identity, restarted,
      session: null, progress: 0, resumed: false });
    try {
      const session = await createOperationalUploadSession({
        original_filename: file.name, size_bytes: file.size, idempotency_key: idempotencyKey,
      });
      if (!mounted.current || sequence !== operation.current) return;
      await begin(file, session, identity, session.state === "uploading");
    } catch (error) {
      if (!mounted.current || sequence !== operation.current) return;
      setTransfer((current) => current ? { ...current, state: "error", error: uploadErrorMessage(error) } : current);
    }
  }

  async function acceptFile(file: File | undefined) {
    setValidationError(null);
    if (!file) return;
    if (!isAcquisitionArchive(file)) {
      setValidationError("Choose one non-empty .tgz acquisition archive. EDF and RKNS files are not accepted here.");
      return;
    }
    operation.current += 1;
    const sequence = operation.current;
    finalizationRequest.current?.abort();
    setTransfer({ state: "preparing", file, idempotencyKey: "", identity: "", session: null, progress: 0, resumed: false });
    try {
      await activeUpload.current?.abort(false);
      if (!mounted.current || sequence !== operation.current) return;
      const identity = await archiveIdentity(file);
      if (!mounted.current || sequence !== operation.current) return;
      await prepare(file, durableIdempotencyKey(file, identity), identity);
    } catch (error) {
      if (!mounted.current || sequence !== operation.current) return;
      setTransfer((current) => current ? { ...current, state: "conflict", error: uploadErrorMessage(error) } : current);
    }
  }

  async function restartStale() {
    if (!transfer?.session) return;
    operation.current += 1;
    const sequence = operation.current;
    try {
      setTransfer((current) => current ? { ...current, state: "reconciling", error: undefined } : current);
      const latest = await getOperationalUploadSession(transfer.session.upload_ref);
      if (!mounted.current || sequence !== operation.current) return;
      if (latest.state !== "uploading" && latest.state !== "expired") {
        await begin(transfer.file, latest, transfer.identity);
        return;
      }
      if (latest.state === "uploading") {
        // Recheck the resource immediately before deliberately replacing it.
        const response = await fetch(transportUrl(latest.tus.upload_url!), {
          method: "HEAD", headers: { Authorization: `Bearer ${pb.authStore.token}`, "Tus-Resumable": "1.0.0" }, cache: "no-store",
        });
        if (!mounted.current || sequence !== operation.current) return;
        if (response.status !== 404 && response.status !== 410) {
          await begin(transfer.file, latest, transfer.identity);
          return;
        }
      }
      // Retire only this session's fingerprint, never all browser uploads.
      const upload = activeUpload.current;
      for (const entry of await upload?.findPreviousUploads() || []) {
        if (entry.metadata.upload_ref === latest.upload_ref) await upload?.options.urlStorage?.removeUpload(entry.urlStorageKey);
      }
      if (!mounted.current || sequence !== operation.current) return;
      await prepare(transfer.file, durableIdempotencyKey(transfer.file, transfer.identity, true), transfer.identity, true);
    } catch (error) {
      if (!mounted.current || sequence !== operation.current) return;
      setTransfer((current) => current ? { ...current, state: "error", error: uploadErrorMessage(error) } : current);
    }
  }

  function handleFile(event: ChangeEvent<HTMLInputElement>) {
    void acceptFile(event.target.files?.[0]);
    event.target.value = "";
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    if (!busy) void acceptFile(event.dataTransfer.files?.[0]);
  }

  async function pause() {
    if (!activeUpload.current) return;
    await activeUpload.current.abort(false);
    setTransfer((current) => current ? { ...current, state: "paused" } : current);
  }

  function retry() {
    if (!transfer) return;
    operation.current += 1;
    if (transfer.session) void begin(transfer.file, transfer.session, transfer.identity, true);
    else void prepare(transfer.file, transfer.idempotencyKey, transfer.identity);
  }

  const busy = Boolean(transfer && ["preparing", "discovering", "uploading", "resuming", "retrying", "reconciling", "finalizing"].includes(transfer.state));
  return (
    <section className="test-tools-panel" aria-labelledby="test-upload-title">
      <header><p>Phase-A acquisition path</p><h3 id="test-upload-title">Upload recording archive</h3>
        <span>Operator test tool · accepted recordings enter the normal processing flow.</span></header>
      <>
        <div className="test-upload-dropzone" onDragOver={(event) => event.preventDefault()} onDrop={handleDrop}>
          <input ref={fileInput} className="sr-only" type="file" accept=".tgz,application/gzip" onChange={handleFile} disabled={busy} />
          <strong>Drop a .tgz acquisition archive</strong><span>or choose the same archive format exported by the mobile workflow</span>
          <Button size="sm" variant="secondary" onClick={() => fileInput.current?.click()} disabled={busy}>{busy ? <Spinner /> : null}Choose .tgz</Button>
        </div>
        {validationError && <Alert>{validationError}</Alert>}
        {transfer && <div className="test-upload-transfer" aria-live="polite">
          <div><strong>{transfer.file.name}</strong><span>{fileSize(transfer.file.size)}</span></div>
          <p>
            {transfer.state === "preparing" && "Preparing authorized upload session…"}
            {transfer.state === "discovering" && "Checking for a resumable browser transfer…"}
            {transfer.restarted && <span>Stale upload restarted · </span>}
            {transfer.state === "resuming" && "Resuming the saved upload…"}
            {transfer.state === "retrying" && "Retrying after a connection interruption…"}
            {transfer.state === "reconciling" && "Checking server upload state…"}
            {transfer.state === "stale" && "The saved transfer is no longer available."}
            {transfer.state === "conflict" && "This upload needs an operator check."}
            {transfer.state === "uploading" && `${transfer.resumed ? "Resumed · " : ""}${Math.round(transfer.progress)}% uploaded`}
            {transfer.state === "paused" && "Paused safely in this browser."}
            {transfer.state === "finalizing" && "Archive transferred · awaiting server acceptance…"}
            {transfer.state === "accepted" && "Accepted · the recording now follows normal Operational processing."}
            {transfer.state === "error" && "Upload needs attention."}
          </p>
          {["uploading", "paused", "finalizing", "accepted"].includes(transfer.state) && <Progress value={transfer.progress} />}
          {transfer.state === "uploading" && <Button size="sm" variant="ghost" onClick={() => void pause()}>Pause</Button>}
          {(transfer.state === "paused" || transfer.state === "error") && <Button size="sm" variant="secondary" onClick={retry}>Retry / resume</Button>}
          {transfer.state === "stale" && <Button size="sm" variant="secondary" onClick={() => void restartStale()}>Start fresh upload</Button>}
          {transfer.error && <Alert>{transfer.error}</Alert>}
        </div>}
      </>
    </section>
  );
}
